'use client';
import Link from 'next/link';
import { FC, useMemo, useState } from 'react';

import Text from '@/components/Text';

import Modal, { ModalPropTypes } from '@/layouts/Modal';
import { sleep } from '@/utils/helper';
import OtpCode from './OtpCode';
import PersonalDetails from './PersonalDetails';
import LoginWithMobileNumber from './LoginWithMobileNumber';
import { useUser } from '@/context/userContext';

interface IndexPropTypes {
  index?: number;
  setIndex: (T: number) => void;
}

export interface ComponentPropTypes extends ModalPropTypes, IndexPropTypes {
  onClose: (...args: any) => void;
  loginRedirect?: string;
  message?: string;
  source?: any;
  data?: any;
  dataKey?: string;
  page?: 'kitchen-studio' | 'checkout';
}

const STEPS = [
  { title: '', component: LoginWithMobileNumber },
  { title: '', component: OtpCode },
  { title: 'Personal Details', component: PersonalDetails },
];

const LoginSignupFlow: FC = () => {
  const {
    showLoginModal,
    setShowLoginModal,
    loginRedirect,
    loginEventMetadata,
    asGuest,
    setLoginEventMetadata,
    setLoginModalMessage,
    setLoginRedirect,
    setAsGuest,
  } = useUser();
  const onClose = () => {
    setShowLoginModal(false);
    setTimeout(() => {
      setLoginModalMessage('');
      setLoginRedirect('');
      setAsGuest(false);
    }, 300); // Reset the message when modal closes
    setLoginEventMetadata(undefined);
  };
  const [index, setIndex] = useState<number>(0);
  const Component = useMemo(() => STEPS[index]?.component, [index]);

  const onModalClose = async () => {
    onClose();
    await sleep(1000);
    setIndex(0); // reset
  };

  const commonProps = {
    index,
    setIndex,
    onClose: onModalClose,
    loginRedirect,
    source: loginEventMetadata?.source,
  };

  return (
    <>
      <Modal
        show={showLoginModal}
        onClose={onModalClose}
        showCloseButton={index !== 1}
        id='login-signup'
        title={STEPS[index]?.title}
        style='rounded-t-3xl rounded-b-none'>
        <>
          <Component {...commonProps} />
          {index !== 1 && <ConsentText onClose={onModalClose} />}
          {index === 0 && asGuest && (
            <ContinueAsGuest
              onClose={onModalClose}
              redirectTo={loginRedirect}
            />
          )}
        </>
      </Modal>
    </>
  );
};

export default LoginSignupFlow;

const ConsentText: FC<ModalPropTypes> = ({ onClose }) => {
  return (
    <div aria-label='terms-privacy-consent' className='mt-3'>
      <Text className='px-0 text-center text-sm'>
        By proceeding, I accept the
        <span className='mx-1'>
          <Link
            href='/policies/terms-services/'
            className='text-fig underline'
            onClick={onClose}>
            Terms & Conditions
          </Link>
        </span>
        and
        <span className='ml-1'>
          <Link
            href='/policies/privacy-policy/'
            className='text-fig underline'
            onClick={onClose}>
            Privacy & Policy
          </Link>
        </span>
      </Text>
    </div>
  );
};

type ContinueAsGuestPropTypes = {
  onClose: (value: any) => void;
  redirectTo: string;
};

const ContinueAsGuest: FC<ContinueAsGuestPropTypes> = ({
  onClose,
  redirectTo,
}) => {
  const borderClassName = 'h-0.5 w-full bg-black/10';
  return (
    <div className='space-y-6'>
      <Text className='flex items-center text-black/60'>
        <span className={borderClassName} />
        <span className='px-2'>OR</span>
        <span className={borderClassName} />
      </Text>
      <div className='space-y-4'>
        <Link
          onClick={onClose}
          href={redirectTo}
          className='btn primary-btn block w-full border border-fig bg-transparent text-fig hover:text-white'>
          Checkout as Guest
        </Link>
      </div>
    </div>
  );
};
